import { useState } from "react";
import { Button } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  useCreateRazorpayOrderMutation,
  useVerifyRazorpayPaymentMutation,
  useRazorpayUserDroppedMutation,
} from "../slices/razorpayPaymentSlice";

// Load the Razorpay Standard Checkout script once.
const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

/**
 * Reusable Razorpay "Pay Now" button.
 *
 * Props:
 *  - gym: object with { _id, name, email, primaryContact, subscriptionType }
 *         (used only for the modal display/prefill; the charged amount and the
 *          gym are derived server-side from the JWT, not from these values)
 *  - token: auth token sent to the backend
 *  - associateId: optional, forwarded to the order endpoint
 *  - selectedPackage: optional package key (e.g. "monthly", "quarterly",
 *    "halfYearly", "yearly") forwarded to the order endpoint
 *  - onSuccess: optional callback fired after a verified payment
 *  - label, size, variant, style: button appearance overrides
 */
const RazorpayPayButton = ({
  gym,
  token,
  associateId,
  selectedPackage,
  onSuccess,
  label = "Pay Now",
  size = "sm",
  variant = "primary",
  style,
}) => {
  const [createRazorpayOrder] = useCreateRazorpayOrderMutation();
  const [verifyRazorpayPayment] = useVerifyRazorpayPaymentMutation();
  const [razorpayUserDropped] = useRazorpayUserDroppedMutation();

  // Guards against double-clicks creating duplicate orders / modals.
  const [processing, setProcessing] = useState(false);

  const handleClick = async () => {
    if (processing) return;
    setProcessing(true);
    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error("Failed to load Razorpay. Check your connection.");
        return;
      }

      // 1) Create the order on the backend.
      const orderData = await createRazorpayOrder({
        payload: { _id: gym._id, associateId, selectedPackage },
        token,
      }).unwrap();

      if (!orderData?.order_id || !orderData?.key_id) {
        toast.error("Could not start payment. Please try again.");
        return;
      }

      // True once Razorpay has actually taken a payment (the handler fired),
      // regardless of whether our backend verification then succeeds. Used so a
      // programmatic close() is never mistaken for a user-initiated dismiss.
      let paymentHandled = false;

      // 2) Open the Razorpay checkout modal.
      const options = {
        key: orderData.key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        name: gym?.name || "Gym Subscription",
        description: `Subscription - ${gym?.subscriptionType || ""}`,
        order_id: orderData.order_id,
        prefill: {
          name: gym?.name || "",
          email: gym?.email || "",
          contact: gym?.primaryContact?.toString() || "",
        },
        notes: { gym_id: `${gym?._id}` },
        theme: { color: "#3399cc" },
        // 3) On success, verify the signature on the backend.
        handler: async (response) => {
          // A payment was taken; never treat the upcoming close() as a drop.
          paymentHandled = true;
          try {
            const verifyRes = await verifyRazorpayPayment({
              payload: {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              },
              token,
            }).unwrap();

            if (verifyRes?.success) {
              toast.success("Payment successful!");
              if (typeof onSuccess === "function") onSuccess();
            } else {
              toast.error("Payment verification failed.");
            }
          } catch (err) {
            toast.error(
              err?.data?.error ||
                "Payment verification failed. Contact support."
            );
          } finally {
            // Tear down the checkout iframe so it stops background polling.
            rzp.close();
          }
        },
        modal: {
          ondismiss: async () => {
            // If a payment was already taken (handler fired), this dismiss is
            // our programmatic close(), not a real user cancellation.
            if (paymentHandled) return;
            toast.info("Payment cancelled.");
            // Razorpay has no abandonment webhook, so record the drop here.
            try {
              await razorpayUserDropped({
                payload: { razorpay_order_id: orderData.order_id },
                token,
              }).unwrap();
            } catch {
              // best-effort; ignore failures
            }
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (response) => {
        // Keep the modal open so the user can retry; Razorpay shows its own UI.
        toast.error(
          response?.error?.description || "Payment failed. Please try again."
        );
      });
      rzp.open();
    } catch (error) {
      toast.error(
        error?.data?.error || "Unable to initiate payment. Please try again."
      );
    } finally {
      // The modal overlay now blocks interaction; re-enable the button so it is
      // usable again after the modal closes (cancel / failure / retry).
      setProcessing(false);
    }
  };

  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      style={style}
      disabled={processing}
      onClick={handleClick}
    >
      {processing ? "Please wait…" : label}
    </Button>
  );
};

export default RazorpayPayButton;
